import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const numFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const qtyFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

// Validated pair (CVD dE 32.3, normal-vision dE 38.3, contrast >= 3:1).
const SPECIES_COLOUR = { VM: "#2563eb", BT: "#d97706" };
const UNMAPPED_COLOUR = "#94a3b8";
const colourFor = (code) => SPECIES_COLOUR[code] || UNMAPPED_COLOUR;

function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function StatTile({ label, value, sub, accent, muted }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        {accent ? (
          <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ background: accent }} />
        ) : null}
        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
          {label}
        </div>
      </div>
      <div className={`mt-2 text-2xl font-semibold ${muted ? "text-slate-400" : "text-slate-900"}`}>
        {value}
      </div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

function SpeciesTable({ title, subtitle, rows, countKey, countLabel }) {
  const max = Math.max(1, ...rows.map((r) => r.mcs || 0));
  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="mt-1 text-xs text-slate-500">{subtitle}</div>
      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-4">Species</th>
              <th className="py-2 pr-4 text-right">Lines</th>
              <th className="py-2 pr-4 text-right">{countLabel}</th>
              <th className="py-2 pr-4 text-right">MCS</th>
              <th className="py-2 pr-4 text-right">Share</th>
              <th className="py-2 pr-4 w-32"> </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.species} className="border-b last:border-0">
                <td className="py-2 pr-4">
                  <span className="flex items-center gap-2">
                    <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-full"
                          style={{ background: colourFor(r.species) }} />
                    <span className="font-medium text-slate-900">{r.species}</span>
                    <span className="text-slate-500">{r.species_name}</span>
                  </span>
                </td>
                <td className="py-2 pr-4 text-right">{numFmt.format(r.lines)}</td>
                <td className="py-2 pr-4 text-right text-slate-600">
                  {numFmt.format(r[countKey] ?? 0)}
                </td>
                <td className="py-2 pr-4 text-right font-medium text-slate-900">
                  {qtyFmt.format(r.mcs)}
                </td>
                <td className="py-2 pr-4 text-right">{r.share_pct}%</td>
                <td className="py-2 pr-4">
                  <div className="h-2 w-full rounded bg-slate-100">
                    <div className="h-2 rounded"
                         style={{ width: `${(r.mcs / max) * 100}%`, background: colourFor(r.species) }} />
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr><td className="py-3 text-slate-500" colSpan={6}>No records in this period.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SpeciesAnalysisDetail() {
  const navigate = useNavigate();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [species, setSpecies] = useState("");
  const [area, setArea] = useState("sales");

  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const body = useMemo(() => ({
    from_date: fromDate || "", to_date: toDate || "", species: species || "",
  }), [fromDate, toDate, species]);

  const detailBody = useMemo(() => ({ ...body, area }), [body, area]);

  useEffect(() => {
    let active = true;
    setLoading(true); setError("");
    api.post("/ai/ceo-chat/species-analysis", body, { timeout: 180000 })
      .then((res) => { if (active) setSummary(res.data || null); })
      .catch((e) => {
        if (!active) return;
        setSummary(null);
        setError(e?.response?.data?.detail || e?.message ||
          "The species split could not be loaded.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [body]);

  useEffect(() => {
    let active = true;
    setDetailLoading(true);
    api.post("/ai/ceo-chat/species-analysis/detail", detailBody, { timeout: 180000 })
      .then((res) => { if (active) setDetail(res.data || null); })
      .catch(() => { if (active) setDetail(null); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [detailBody]);

  const rows = useMemo(() => detail?.rows || [], [detail]);
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.Reference, r.Consignee, r.Country, r.Grade, r.Form, r.SpeciesName]
        .map((v) => String(v ?? "").toLowerCase()).some((v) => v.includes(q)));
  }, [rows, search]);

  const exportCsv = useCallback(() => {
    const cols = ["Species", "SpeciesName", "Form", "Reference", "RefDate",
      "Grade", "Consignee", "Country", "Mcs"];
    const csv = [cols.map(csvEscape).join(","),
      ...filteredRows.map((r) => cols.map((c) => csvEscape(r?.[c])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `species-analysis-${area}.csv`; a.click();
    URL.revokeObjectURL(url);
  }, [filteredRows, area]);

  const s = summary?.summary || {};
  const opts = summary?.species_options || [];
  const headline = s.headline || {};

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1700px] space-y-5">
        <div className="flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-sm md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Species Analysis — Vannamei vs Black Tiger
            </h1>
            <p className="mt-2 max-w-4xl text-sm text-slate-600">
              How the business splits between the two shrimp species we process.
              Species is taken from the ERP species master and applied through the
              product master, so shipped sales and confirmed orders are counted on
              exactly the same definition.
            </p>
          </div>
          <button type="button"
            onClick={() => navigate("/ceo-command-center")}
            className="shrink-0 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            Back to Dashboard
          </button>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
        ) : null}

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">From Date</span>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2" />
            </label>
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">To Date</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2" />
            </label>
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">Species</span>
              <select value={species} onChange={(e) => setSpecies(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2">
                <option value="">All Species</option>
                {opts.map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.code} — {o.description || o.name}
                  </option>
                ))}
              </select>
            </label>
            <button type="button"
              onClick={() => { setFromDate(""); setToDate(""); setSpecies(""); }}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Reset Filters
            </button>
          </div>
          <div className="mt-3 text-xs text-slate-500">
            Date basis: sales on invoice date ({summary?.date_range?.sales_date_field || "ScrmCustomInvoices.CInvDate"}),
            {" "}orders on order date ({summary?.date_range?.orders_date_field || "ScrmPoData.PoDate"}).
            {" "}{summary?.date_range?.note}
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl border bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
            Loading species split...
          </div>
        ) : summary ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
              <StatTile label="Species Tracked" value={numFmt.format(s.species_count || 0)}
                sub={opts.map((o) => o.code).join(" · ")} />
              {opts.map((o) => (
                <StatTile key={o.code}
                  label={`${o.code} — ${o.description || o.name}`}
                  value={`${headline[o.code]?.sales_share_pct ?? 0}%`}
                  sub={`${qtyFmt.format(headline[o.code]?.sales_mcs ?? 0)} MCS shipped`}
                  accent={colourFor(o.code)} />
              ))}
              <StatTile label="Total Shipped" value={qtyFmt.format(s.sales_total_mcs || 0)}
                sub={`MCS across ${numFmt.format(s.sales_total_invoices || 0)} invoice(s)`} />
              <StatTile label="Unmapped Lines"
                value={numFmt.format((s.unmapped_sales_lines || 0) + (s.unmapped_order_lines || 0))}
                muted={!((s.unmapped_sales_lines || 0) + (s.unmapped_order_lines || 0))}
                sub="Lines whose product does not resolve to a species" />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <SpeciesTable title="Shipped Sales by Species" subtitle="Invoiced volume"
                rows={summary.sales_by_species || []} countKey="invoices" countLabel="Invoices" />
              <SpeciesTable title="Confirmed Orders by Species" subtitle="Approved order book"
                rows={summary.orders_by_species || []} countKey="orders" countLabel="Orders" />
            </div>

            <div className="rounded-2xl border bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Line-level Detail</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {detailLoading ? "Loading lines..."
                      : detail?.truncated
                        ? `Showing ${numFmt.format(filteredRows.length)} of ${numFmt.format(detail.matching_lines || 0)} lines — narrow the filters to see them all`
                        : `${numFmt.format(filteredRows.length)} line(s)`}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex overflow-hidden rounded-xl border border-slate-300">
                    {[["sales", "Shipped Sales"], ["orders", "Confirmed Orders"]].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setArea(v)}
                        className={`px-3 py-2 text-sm font-medium ${
                          area === v ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                  <input value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search reference, consignee, country, grade"
                    className="w-72 rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                  <button type="button" onClick={exportCsv} disabled={!filteredRows.length}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                    Export CSV
                  </button>
                </div>
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">Species</th>
                      <th className="py-2 pr-4">Product Form</th>
                      <th className="py-2 pr-4">{area === "sales" ? "Invoice No" : "PO Number"}</th>
                      <th className="py-2 pr-4">Date</th>
                      <th className="py-2 pr-4">Grade</th>
                      <th className="py-2 pr-4">Consignee</th>
                      <th className="py-2 pr-4">Country</th>
                      <th className="py-2 pr-4 text-right">MCS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.slice(0, 300).map((r, i) => (
                      <tr key={`${r.Reference}-${i}`} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2 pr-4">
                          <span className="flex items-center gap-1.5">
                            <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full"
                                  style={{ background: colourFor(r.Species) }} />
                            {r.Species}
                          </span>
                        </td>
                        <td className="py-2 pr-4">{r.Form}</td>
                        <td className="py-2 pr-4 font-medium text-slate-900">{r.Reference}</td>
                        <td className="py-2 pr-4">{r.RefDate}</td>
                        <td className="py-2 pr-4">{r.Grade}</td>
                        <td className="py-2 pr-4">{r.Consignee}</td>
                        <td className="py-2 pr-4">{r.Country}</td>
                        <td className="py-2 pr-4 text-right">{qtyFmt.format(r.Mcs)}</td>
                      </tr>
                    ))}
                    {!filteredRows.length && !detailLoading ? (
                      <tr><td className="py-4 text-center text-slate-500" colSpan={8}>
                        No lines match the current filters.
                      </td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              {filteredRows.length > 300 ? (
                <div className="mt-2 text-xs text-slate-500">
                  Showing the first 300 rows on screen. Export CSV includes all
                  {" "}{numFmt.format(filteredRows.length)} loaded lines.
                </div>
              ) : null}
              {detail?.totals ? (
                <div className="mt-4 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                  Loaded totals — {numFmt.format(detail.totals.lines)} line(s) ·{" "}
                  {qtyFmt.format(detail.totals.mcs)} MCS
                  {Object.entries(detail.totals.by_species || {}).map(([k, v]) =>
                    ` · ${k}: ${numFmt.format(v.lines)} line(s)`)}
                </div>
              ) : null}
            </div>

            <div className="rounded-2xl border bg-white p-5 text-xs text-slate-600 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">Validation &amp; ERP Reconciliation</div>
              <ul className="mt-2 space-y-1">
                <li><span className="font-medium text-slate-800">Reconciliation:</span>{" "}
                  {summary.reconciliation?.pass ? "Passed" : "Failed"} — {summary.reconciliation?.equation}</li>
                <li><span className="font-medium text-slate-800">Species rule:</span>{" "}
                  {summary.source?.species_rule}</li>
                <li><span className="font-medium text-slate-800">Compatibility:</span>{" "}
                  {summary.compatibility?.pass ? "Passed" : "Failed"} —{" "}
                  {summary.compatibility?.varieties_total} product varieties,{" "}
                  {summary.compatibility?.varieties_without_species} without a species,{" "}
                  {summary.compatibility?.unknown_species_varieties} with an unrecognised species code</li>
              </ul>
              {(summary.notes || []).length ? (
                <div className="mt-3 border-t pt-3">
                  <div className="font-medium text-slate-800">Notes</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5">
                    {summary.notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
